// @file Padlet attachment shape for Tldraw
// To render the Padlet attachment shape in Tldraw. We use a dynamic Vue component to render the Padlet attachment.
import { FallbackAttachmentType, fallbackImageUrl } from '@@/bits//attachments'
import { setupVueApp } from '@@/bits/vue'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import WhiteboardAttachment from '@@/vuecomponents/WhiteboardAttachment.vue'
import type { Link } from '@padlet/arvo'
import { LinkHelpers } from '@padlet/beethoven-client'
import type { ReactElement } from 'react'
import { useEffect } from 'react'
import type { Geometry2d, RecordProps, TLBaseShape, TLOnResizeHandler, TLShapeId } from 'tldraw'
import {
  createShapePropsMigrationIds,
  createShapePropsMigrationSequence,
  HTMLContainer,
  Rectangle2d,
  resizeBox,
  ShapeUtil,
  T,
  toDomPrecision,
  useQuickReactor,
} from 'tldraw'
import 'tldraw/tldraw.css'

type IPadletAttachmentShape = TLBaseShape<
  'padlet-attachment-shape',
  {
    w: number
    h: number
    resizable: boolean
    postId: number
    isPoll: boolean
  }
>

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const versions = createShapePropsMigrationIds(
  // this must match the shape type in the shape definition
  'padlet-attachment-shape',
  {},
)

export const padletAttachmentShapeMigration = createShapePropsMigrationSequence({
  sequence: [],
})

function getVueContainerId(props: IPadletAttachmentShape['props']): string {
  return `tldraw_vue_padlet_attachment_shape_for_post_${props.postId}`
}
function getFallbackAttachmentType(link: Link): FallbackAttachmentType {
  if (link == null) return FallbackAttachmentType.BrokenLink
  if (LinkHelpers.isAudio(link)) return FallbackAttachmentType.Audio
  if (LinkHelpers.isVideo(link)) return FallbackAttachmentType.Video
  if (LinkHelpers.isPhoto(link)) return FallbackAttachmentType.Image
  if (LinkHelpers.isFile(link)) return FallbackAttachmentType.File
  if (LinkHelpers.isExcelSpreadsheet(link)) return FallbackAttachmentType.Spreadsheet
  if (LinkHelpers.isPowerpointPresentation(link)) return FallbackAttachmentType.Presentation
  if (LinkHelpers.isGoogleDoc(link)) return FallbackAttachmentType.GoogleDocument
  if (LinkHelpers.isDocument(link)) return FallbackAttachmentType.TextDocument
  if (LinkHelpers.isPage(link)) return FallbackAttachmentType.Link
  return FallbackAttachmentType.Link
}

/**
 * Get a fake poll SVG to show in the frame browser when the shape is a poll
 * @param shape
 * @returns Poll SVG
 */
function getPollSvg(shape: IPadletAttachmentShape): ReactElement {
  return (
    <svg
      width={shape.props.w}
      height={shape.props.h}
      viewBox={`0 0 ${shape.props.w} ${shape.props.h}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="0" y="0" width="100%" height="100%" fill="#1E1E1E" rx="16" ry="16" />
      <text x="20" y="25" fill="#BFBFBF" font-size="12" className="font-sans">
        Poll
      </text>

      <g className="poll-choice" transform="translate(10, 40)">
        <rect x="0" y="0" width={shape.props.w} height="40" rx="8" ry="8" fill="#1E1E1E" />
        <text x="35" y="25" fill="#BFBFBF" font-size="12" className="font-sans">
          Option 1
        </text>
        <circle cx="20" cy="20" r="8" stroke="#BFBFBF" fill="none" />
      </g>

      <g className="poll-choice" transform="translate(10, 80)">
        <rect x="0" y="0" width={shape.props.w} height="40" rx="8" ry="8" fill="#1E1E1E" />
        <text x="35" y="25" fill="#BFBFBF" font-size="12" className="font-sans">
          Option 2
        </text>
        <circle cx="20" cy="20" r="8" stroke="#BFBFBF" fill="none" />
      </g>

      <g className="poll-choice" transform="translate(10, 120)">
        <rect x="0" y="0" width={shape.props.w} height="40" rx="8" ry="8" fill="#1E1E1E" />
        <text x="35" y="25" fill="#BFBFBF" font-size="12" className="font-sans">
          Option 3
        </text>
        <circle cx="20" cy="20" r="8" stroke="#BFBFBF" fill="none" />
      </g>

      <g className="poll-choice" transform="translate(10, 160)">
        <rect x="0" y="0" width={shape.props.w} height="40" rx="8" ry="8" fill="#1E1E1E" />
        <text x="35" y="25" fill="#BFBFBF" font-size="12" className="font-sans">
          Option 4
        </text>
        <circle cx="20" cy="20" r="8" stroke="#BFBFBF" fill="none" />
      </g>

      <g className="poll-footer" transform={`translate(0, ${shape.props.h - 45})`}>
        <rect x="0" y="0" width={shape.props.w} height="40" fill="#1E1E1E" rx="16" ry="16" />
        <text x="20" y="25" fill="#BFBFBF" font-size="12" className="font-sans">
          Votes • Show Results
        </text>
      </g>
    </svg>
  )
}

export class PadletAttachmentShapeUtil extends ShapeUtil<IPadletAttachmentShape> {
  static override type = 'padlet-attachment-shape' as const
  static override props: RecordProps<IPadletAttachmentShape> = {
    w: T.number,
    h: T.number,
    resizable: T.boolean,
    postId: T.number,
    isPoll: T.boolean,
  }

  static override migrations = padletAttachmentShapeMigration

  getDefaultProps(): IPadletAttachmentShape['props'] {
    return {
      w: 200,
      h: 200,
      resizable: true,
      postId: 0,
      isPoll: false,
    }
  }

  override canBind = (): boolean => true
  override canEdit = (): boolean => false
  override canResize = (): boolean => true
  override isAspectRatioLocked = (shape: IPadletAttachmentShape): boolean => !shape.props.isPoll
  vueApp: Record<TLShapeId, Vue> = {}
  aspectRatios: Record<TLShapeId, number> = {}

  async toSvg(shape: IPadletAttachmentShape): Promise<null | ReactElement> {
    if (shape.props.isPoll) {
      return getPollSvg(shape)
    }
    const post = useSurfacePostsStore().postEntitiesById[shape.props.postId]
    if (post == null) return null
    const url = post.wish_content?.attachment_props?.url ?? post.attachment
    const linkAttributes =
      useSurfaceAttachmentsStore().linkAttributesByUrl[url] ??
      (await useSurfaceAttachmentsStore().fetchAndStoreLinkAttributes(url))
    if (linkAttributes == null) return <rect width={shape.props.w} height={shape.props.h} fill="#1d1d1d" rx="12" />
    let currentImageUrl = linkAttributes?.preview_image?.url
    currentImageUrl ??= fallbackImageUrl(getFallbackAttachmentType(linkAttributes), 'dark', null)
    if (currentImageUrl == null) {
      return <rect width={shape.props.w} height={shape.props.h} fill="#1d1d1d" rx="12" />
    }
    return (
      <image
        width={shape.props.w}
        height={shape.props.h}
        href={currentImageUrl}
        preserveAspectRatio="xMidYMin slice"
        clip-path="inset(0% round 12px)"
      />
    )
  }

  getGeometry(shape: IPadletAttachmentShape): Geometry2d {
    const geometry = new Rectangle2d({
      width: shape.props.w,
      height: shape.props.h,
      isFilled: true,
    })
    return geometry
  }

  override onResize: TLOnResizeHandler<any> = (shape: IPadletAttachmentShape, info) => {
    if (!shape.props.resizable) return shape
    if (shape.props.isPoll) {
      const pollHtmlElement = document.getElementById(shape.id)?.children[0]?.children[0]
      let height = 200
      if (pollHtmlElement != null) {
        height = pollHtmlElement.clientHeight
      }
      return resizeBox(shape, info, { minHeight: height, maxHeight: height })
    }
    return resizeBox(shape, info)
  }

  component(shape: IPadletAttachmentShape): JSX.Element {
    // Initial setup and mounting of the Vue component
    const editorZoom = this.editor.getZoomLevel()
    // const _this = this

    const createVueApp = async (): Promise<void> => {
      const width = shape.props.w
      const height = shape.props.h
      const app = await setupVueApp({
        el: `#${getVueContainerId(shape.props)}`,
        usePinia: false,
        disableAccessibilityChecks: true,
        rootComponent: WhiteboardAttachment,
        otherVueOptions: {
          parent: (window as any).app,
          data: function () {
            return {
              postId: shape.props.postId,
              width,
              height,
              zoom: editorZoom,
            }
          },
          render: function (h: any) {
            return h(WhiteboardAttachment, {
              props: {
                postId: this.postId,
                width: this.width,
                height: this.height,
                zoom: this.zoom,
              },
              on: {
                loaded: this.handleOnLoaded,
              },
            })
          },
          methods: {
            handleOnLoaded: function () {},
          },
        },
      })
      this.vueApp[shape.id] = app as Vue
    }
    useEffect(() => {
      void createVueApp()
      return () => {
        this.vueApp[shape.id]?.$destroy()
      }
    }, [])

    // Update the Vue component when the shape props change
    useQuickReactor('update vue component zoom', () => {
      const editorZoom = this.editor.getZoomLevel()
      if (this.vueApp[shape.id] == null) return
      this.vueApp[shape.id].$data.zoom = editorZoom
    })

    useEffect(() => {
      const editorZoom = this.editor.getZoomLevel()
      const width = shape.props.w
      const height = shape.props.h
      if (this.vueApp[shape.id] == null) return
      this.vueApp[shape.id].$data.postId = shape.props.postId
      this.vueApp[shape.id].$data.width = width
      this.vueApp[shape.id].$data.height = height
      this.vueApp[shape.id].$data.zoom = editorZoom
    }, [shape.props.postId, shape.props.w])

    return (
      <HTMLContainer id={shape.id}>
        <div id={getVueContainerId(shape.props)}></div>
      </HTMLContainer>
    )
  }

  indicator(shape: IPadletAttachmentShape): JSX.Element | null {
    const bounds = this.getGeometry(shape).bounds
    return <rect width={toDomPrecision(bounds.width)} height={toDomPrecision(bounds.height)} />
  }
}

export default PadletAttachmentShapeUtil
